import { useEffect } from 'react';
import { useAtom } from 'jotai';
import { useIntl } from 'react-intl';
import { Divider } from '@packages/shared/src/ui/Divider/Divider';
import { selectedQuantity } from '../atoms';
import type { GetValidQuantityProps } from './getValidQuantity/getValidQuantity';
import { getValidQuantity } from './getValidQuantity/getValidQuantity';
import { quantitySelectionMessages } from './messages';
import { getValidValues } from './getValidValues/getValidValues';
import { ActionButton } from './ActionButton';

const elementsHeight = '1.75rem';

export type QuantitySelectionProps = {
  /**
   * Sets the maximum value the quantity can be.
   * @default 10
   */
  max?: number;
  /**
   * Sets the minimum value the quantity can be.
   * @default 1
   */
  min?: number;
  /**
   * Sets mow much the quantity of the step will be increased/decreased on button click.
   * @default 1
   */
  step?: number;
  /**
   * Get the quantity of the lineItem for miniDV
   * @default 1
   */
  lineItemQuantity?: number;
};

/**
 * <p>
 * This component is used to display some quantity. It comes with an increase and decrease button.
 * With the given props, you can adjust the <code>step</code> value with which the quantity will be decreased/increased.
 * You also can set a <code>min</code>/<code>max</code> value for the quantity and define the initial quantity with the <code>quantity</code> prop.
 * </p>
 * */
export const QuantitySelection = ({
  step = 1,
  min = 1,
  max = 10,
  lineItemQuantity,
  ...props
}: QuantitySelectionProps) => {
  const values = getValidValues({
    step,
    min,
    max,
  });
  const [quantity, setQuantityAtom] = useAtom(selectedQuantity);
  const { formatMessage } = useIntl();

  // Quantity will be updated, if user uses miniDV
  useEffect(() => {
    if (lineItemQuantity) {
      setQuantityAtom(lineItemQuantity);
    }
  }, []);

  const setQuantity = (type: GetValidQuantityProps['type']) => {
    setQuantityAtom(
      getValidQuantity({
        currentQuantity: quantity,
        step: values.step,
        ...(type === 'increase'
          ? { type: 'increase', max: values.max }
          : { type: 'decrease', min: values.min }),
      }),
    );
  };

  return (
    <>
      <Divider />
      <div {...props} className="text-size-body1 my-1.5 flex items-center justify-start">
        <div className="mx-2 select-none">{formatMessage(quantitySelectionMessages.label)}</div>
        <ActionButton
          type="Decrease"
          disabled={quantity <= values.min}
          onClick={() => {
            setQuantity('decrease');
          }}
          height={elementsHeight}
        />
        <div
          style={{
            height: elementsHeight,
            width: '3.5rem',
            lineHeight: elementsHeight,
          }}
          className="border-color-grey-dark mx-1.5 select-none overflow-hidden rounded-[3px] border text-center"
        >
          {quantity}
        </div>
        <ActionButton
          type="Increase"
          disabled={quantity >= values.max}
          onClick={() => {
            setQuantity('increase');
          }}
          height={elementsHeight}
        />
      </div>
      <Divider />
    </>
  );
};
